import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import isEqual from 'lodash/isEqual';
import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth/withWidth';
import Card from '@material-ui/core/Card/Card';
import CardContent from '@material-ui/core/CardContent/CardContent';
import Typography from '@material-ui/core/Typography/Typography';
import Button from '@material-ui/core/Button/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup/ButtonGroup';
import CardActions from '@material-ui/core/CardActions/CardActions';
import Grid from '@material-ui/core/Grid/Grid';
import grey from '@material-ui/core/colors/grey';
import Dialog from '@material-ui/core/Dialog/Dialog';
import DialogContent from '@material-ui/core/DialogContent/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions/DialogActions';
import Icon from '@material-ui/core/Icon/Icon';
import RootRef from '@material-ui/core/RootRef/RootRef';
import { InView } from 'react-intersection-observer';
import { debounce } from '@material-ui/core';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import AnalyticsWrapper from '@configuratorware/configurator-frontendgui/App/Shared/Components/Analytics/AnalyticsWrapper';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import MainButton from '@configuratorware/configurator-frontendgui/Shared/Components/MainButton';
import PriceOverview from '@configuratorware/configurator-frontendgui/App/Shared/Components/PriceOverview';
import Notification from '@configuratorware/configurator-frontendgui/App/Shared/Components/Notification';
import PriceList from '@configuratorware/configurator-frontendgui/App/Shared/Components/PriceList';
import AddToCart from '@configuratorware/configurator-frontendgui/App/Shared/Components/AddToBasket';
import ReceiveOfferForm from '@configuratorware/configurator-frontendgui/App/Shared/Components/ReceiveOfferForm';
import AcceptDesign from '@configuratorware/configurator-frontendgui/App/Shared/Components/AcceptDesign';
import LoadingOverlay from '@configuratorware/configurator-frontendgui/Framework/Components/LoadingOverlay';
import AcceptPrivacy from '@configuratorware/configurator-frontendgui/App/Shared/Components/AcceptPrivacy';
import { lightGray } from '../../Constants/Colors';
import { getConf } from '@configuratorware/configurator-frontendgui/App/configuration';

const styles = (theme) => ({
  wrapper: {
    [theme.orientation.portrait]: {
      maxHeight: '100%',
    },
  },
  contentPositioning: {
    paddingLeft: 32,
    paddingRight: 20,
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 0,
      paddingRight: 8,
      marginRight: '-8px',
    },
    '& p': {
      fontSize: 15,
      letterSpacing: 'normal',
    },
    marginRight: 4,
    padding: 0,
  },
  dialogWrapper: {
    borderTop: '7px solid ' + theme.palette.primary.main,
    padding: '0 32px',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      padding: '0 16px',
    },
  },
  card: {
    marginBottom: 1,
    borderTop: '7px solid ' + theme.palette.primary.main,
    padding: 15,
    overflow: 'visible',
    [theme.breakpoints.between('xs', 'lg')]: {
      marginTop: 16,
    },
    [theme.breakpoints.down('xs')]: {
      overflow: 'hidden',
      display: 'flex',
      boxShadow: 'none',
      '&:last-child': {
        padding: '12px 0 0',
      },
      maxHeight: '50%',
      paddingTop: 0,
      borderTop: '1px solid ' + grey[500],
      borderRadius: 0,
    },
  },
  cardUnboxed: {
    borderTop: 'none',
    boxShadow: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
    },
  },
  backButton: {
    backgroundColor: lightGray,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    padding: '6px 15px',
    width: '100%',
    [theme.orientation.landscape]: {
      marginTop: 16,
      height: 40,
    },
    [theme.orientation.portrait]: {
      width: '90%',
      borderRadius: '5px !important',
      border: '1px solid black',
    },
    fontSize: '15px',
  },
  cardBulk: {
    marginBottom: 1,
    padding: 15,
    overflow: 'visible',
    backgroundColor: lightGray,
    [theme.breakpoints.between('xs', 'lg')]: {
      marginTop: 16,
    },
    [theme.orientation.portrait]: {
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      width: '100%',
      left: 0,
      boxShadow: 'none',
      '&:last-child': {
        padding: '12px 0 0',
      },
      paddingTop: 0,
      borderTop: '1px solid ' + grey[500],
      borderTopRightRadius: 0,
      borderTopLeftRadius: 0,
      backgroundColor: 'white',
      [theme.breakpoints.up('sm')]: {
        border: 'none',
      },
    },
  },
  cardUnboxedBulk: {
    borderTop: 'none',
    boxShadow: 'none',
    [theme.orientation.portrait]: {
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: -80,
      width: '100%',
    },
  },
  cardContentWrapper: {
    [theme.orientation.portrait]: {
      position: 'relative',
      '&:last-child': {
        paddingBottom: 4,
      },
    },
  },
  bulkOuterGrid: {
    [theme.orientation.portrait]: {
      padding: 15,
      borderRadius: 4,
      backgroundColor: lightGray,
      marginBottom: 12,
      boxShadow:
        '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    },
  },
  bulkPriceContent: {
    backgroundColor: 'white',
    paddingBottom: '16px!important',
    marginBottom: 8,
  },
  bulkPriceYourCurrentPrice: {
    width: '100%',
    fontSize: '1.5rem',
    textAlign: 'right',
    [theme.orientation.portrait]: {
      paddingTop: 20,
    },
  },
  bulkPriceCurrentPrice: {
    width: '100%',
    fontSize: '1.75rem',
    fontWeight: 'bold',
    textAlign: 'right',
    paddingBottom: 10,
  },
  bulkPriceYourCurrentNetTotal: {
    width: '100%',
    fontSize: '1.25rem',
    textAlign: 'right',
  },
  bulkPriceNetTotalFormatted: {
    width: '100%',
    fontSize: '1.25rem',
    fontWeight: 'bold',
    textAlign: 'right',
    paddingBottom: 10,
  },
  bulkPriceRowFirst: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: `2px solid ${lightGray}`,
    paddingBottom: 8,
    fontSize: '1rem',
  },
  bulkPriceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: `2px solid ${lightGray}`,
    paddingBottom: 8,
    paddingTop: 8,
    fontSize: '1.25rem',
  },
  bulkPriceRowLast: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingTop: 8,
    fontSize: '1rem',
  },
  bulkPriceTotal: {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  bulkPiecesContainer: {
    paddingTop: 8,
  },
  bulkPriceAmountFrom: {
    fontSize: '1rem',
  },
  topSpacer: {
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: -12,
      height: 30,
      width: '100%',
      backgroundColor: 'white',
    },
  },
  AmountPriceContent: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 5,
    },
  },
  dialogPositioning: {
    overflow: 'hidden',
    '& .MuiDialog-container': {
      '& .MuiDialogTitle-root': {
        paddingTop: 25,
        paddingBottom: 15,
        '& p, h2': {
          fontSize: 24,
          fontWeight: 'bold',
          letterSpacing: 'normal',
        },
      },
      '& .MuiDialogActions-root': {
        paddingBottom: 24,
        [theme.breakpoints.down('xs')]: {
          marginRight: 0,
        },
        marginRight: 8,
      },
      '& .MuiDialogTitle-root, .MuiDialogActions-root': {
        paddingLeft: 32,
        paddingRight: 32,
        [theme.breakpoints.down('xs')]: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        '& p': {
          fontSize: 15,
          letterSpacing: 'normal',
        },
      },
    },
    '& .MuiButton-root': {
      height: 40,
      textTransform: 'none',
      minWidth: 140,
      marginTop: 8,
      color: theme.palette.primary.darkText,
      [theme.breakpoints.down('xs')]: {
        height: 56,
      },
    },
  },
  AmountPriceButton: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    margin: 0,
    padding: '6px 15px',
    [theme.orientation.portrait]: {
      minWidth: 35,
      padding: 0,
      marginLeft: 15,
    },
    color: theme.palette.primary.darkText,
  },
  smallButtonSize: {
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
      lineHeight: 1,
    },
  },
  mainButton: {
    margin: 0,
    padding: '6px 15px',
    height: 40,
    lineHeight: 1.5,
    [theme.breakpoints.down('xs')]: {
      height: 56,
    },
    [theme.orientation.portrait]: {
      width: '90%',
    },
  },
  mainButtonLabel: {
    [theme.breakpoints.down('xs')]: {
      whiteSpace: 'normal',
      fontSize: 12,
    },
  },
  mblButtonGroup: {
    width: '100%',
  },
  AmountPriceTypography: {
    textAlign: 'end',
    lineHeight: 1.2,
    [theme.breakpoints.down('xs')]: {
      padding: '3px 0',
      textAlign: 'start',
      fontSize: 12,
      lineHeight: 1,
    },
  },
  AmountPriceTypographyVat: {
    fontSize: '.8rem',
    lineHeight: '1rem',
    textAlign: 'end',
    display: 'block',

    [theme.orientation.portrait]: {
      fontSize: 9,
      textAlign: 'right',
    },
  },
  cardActions: {
    padding: '8px 0',
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },
  },
  closeIcon: {
    position: 'absolute',
    right: 32,
    top: 32,
    [theme.breakpoints.down('xs')]: {
      right: 16,
    },
    '&:hover': {
      cursor: 'pointer',
    },
  },
  dialogTitleRoot: {
    padding: '20px 0',
  },
});

const DialogActionsWrapper = ({ onTouchMove, ...props }) => {
  const ref = useRef();

  useEffect(() => {
    const { current } = ref;
    if (current) {
      current.addEventListener('touchmove', onTouchMove, { passive: false });
    }
    return () => {
      if (current) {
        current.removeEventListener('touchmove', onTouchMove, { passive: false });
      }
    };
  }, [onTouchMove]);
  return (
    // as of material-ui v3.x the refs are not forwarded, so the only way to get it is using RootRef
    // FIXME check after updating material-ui
    <RootRef rootRef={ref}>
      <DialogActions {...props} />
    </RootRef>
  );
};

DialogActionsWrapper.propTypes = {
  onTouchMove: PropTypes.func,
};

class AmountPrice extends React.Component {
  static propTypes = {
    boxed: PropTypes.bool,
    classes: PropTypes.object,
    amount: PropTypes.number,
    priceFormatted: PropTypes.string,
    vatNotice: PropTypes.string,
    ProductVariantComponent: PropTypes.elementType.isRequired,
    ConfigurationModeSwitchComponent: PropTypes.elementType.isRequired,
    showConfigurationModeSwitch: PropTypes.bool,
    error: PropTypes.object,
    width: PropTypes.string,
    hidePrices: PropTypes.bool,
    hidePieces: PropTypes.bool,
    priceList: PropTypes.array,
    showProductVariants: PropTypes.bool,
    title: PropTypes.string,
    showCallToAction: PropTypes.bool,
    showReceiveOfferForm: PropTypes.bool,
    onClose: PropTypes.func,
    sendReceiveOfferRequest: PropTypes.func,
    setConfigurationComment: PropTypes.func,
    onSelectableChanged: PropTypes.func,
    onAddToBasket: PropTypes.func,
    isBulkNamesSelected: PropTypes.bool,
    clientTexts: PropTypes.object,
    bulkSavings: PropTypes.array,
    notifications: PropTypes.array,
    displayAcceptDesign: PropTypes.bool,
    bulkNameErrors: PropTypes.array,
    onShowChange: PropTypes.func,
    wrapperClass: PropTypes.string,
    validateConfiguration: PropTypes.func,
    addToCartPopUpLoader: PropTypes.bool,
    ReceiveOfferFormComponent: PropTypes.elementType,
    AcceptDesignComponent: PropTypes.elementType,
    PriceOverviewComponent: PropTypes.elementType,
    NotificationComponent: PropTypes.elementType,
    PriceListComponent: PropTypes.elementType,
    AddToCartComponent: PropTypes.elementType,
    renderPriceBox: PropTypes.func,
    userComment: PropTypes.string,
    AcceptPrivacyComponent: PropTypes.elementType,
    bulkPrices: PropTypes.array,
    netTotalFormatted: PropTypes.string,
    orientation: PropTypes.string,
    saveConfiguration: PropTypes.func,
  };

  static defaultProps = {
    showCallToAction: true,
    showConfigurationModeSwitch: false,
    notifications: [],
    clientTexts: {
      termsAndConditionsLink: '',
    },
    hidePieces: false,
    ReceiveOfferFormComponent: ReceiveOfferForm,
    AcceptDesignComponent: AcceptDesign,
    PriceOverviewComponent: PriceOverview,
    NotificationComponent: Notification,
    PriceListComponent: PriceList,
    AddToCartComponent: AddToCart,
    AcceptPrivacyComponent: AcceptPrivacy,
  };

  state = {
    showPriceOverview: false,
    notice: '',
    hasErrorReceiveOfferForm: false,
    bulkNameErrors: [],
    approval: {
      value: false,
      error: null,
    },
    inView: true,
    showErrors: false,
    dataPrivacyAccepted: false,
  };

  componentDidUpdate(prevProps, prevState) {
    const { error, bulkNameErrors } = this.props;
    if (error && !isEqual(error, prevProps.error) && this.dialogContentRef.current) {
      this.dialogContentRef.current.scrollTo(0, 0);
    }
    if (bulkNameErrors !== prevProps.bulkNameErrors) {
      this.setState({ bulkNameErrors });
    }
    if (prevProps.addToCartPopUpLoader && !this.props.addToCartPopUpLoader) {
      this.handleClose();
    }
    const { showPriceOverview } = this.state;
    if (showPriceOverview !== prevState.showPriceOverview) {
      const { onShowChange } = this.props;
      onShowChange && onShowChange(showPriceOverview);
    }
    if (this.state.notice === prevState.notice && this.props.userComment !== this.state.notice) {
      this.setState({ notice: this.props.userComment });
    }
  }

  receiveOfferFormComponentRef = React.createRef();
  dialogContentRef = React.createRef();
  priceListRef = React.createRef();
  acceptDesignRef = React.createRef();
  acceptDataPrivacyRef = React.createRef();

  handleChange = (event) => {
    this.setState({
      [event.target.name]: {
        value: event.target.checked,
        error: null,
      },
    });
  };

  verifyRequestErrors = (hasFormValidationErrors) => {
    const { bulkNameErrors, displayAcceptDesign } = this.props;
    const designApproved = this.state.approval.value;

    if (bulkNameErrors && bulkNameErrors.length > 0) {
      this.setState({ showErrors: true });
      this.setState({ bulkNameErrors });
      this.dialogContentRef.current.scrollTo(0, 0);
    }
    if (displayAcceptDesign && !designApproved) {
      this.setState({
        approval: {
          value: false,
          error: true,
        },
      });
      !hasFormValidationErrors &&
        this.acceptDesignRef.current &&
        this.acceptDesignRef.current.scrollIntoView();
    }
    if (!this.dataPrivacyApproved()) {
      this.setState({
        dataPrivacyAccepted: {
          value: false,
          error: true,
        },
      });
      !hasFormValidationErrors &&
        this.acceptDataPrivacyRef.current &&
        this.acceptDataPrivacyRef.current.scrollIntoView();
    }
  };

  requestHasErrors = () => {
    const { bulkNameErrors, displayAcceptDesign } = this.props;
    const designApproved = this.state.approval.value;
    if (
      (bulkNameErrors && bulkNameErrors.length > 0) ||
      (displayAcceptDesign && !designApproved) ||
      !this.dataPrivacyApproved()
    ) {
      return true;
    }
    return false;
  };

  onAddToBasket = () => {
    const { onAddToBasket } = this.props;
    if (!this.requestHasErrors()) {
      onAddToBasket();
    } else {
      this.verifyRequestErrors();
    }
  };

  dataPrivacyApproved = () => {
    const { showReceiveOfferForm, clientTexts } = this.props;
    const { dataPrivacyLink } = clientTexts;
    const dataPrivacyApproved = this.state.dataPrivacyAccepted.value;
    return !dataPrivacyLink || !showReceiveOfferForm || dataPrivacyApproved;
  };

  handleTouchMove = ((evt) => {
    const dialogContent = this.dialogContentRef.current;
    evt.preventDefault();
    dialogContent.scrollTo(0, dialogContent.scrollTop);
  }).bind(this);

  openPriceOverview = async () => {
    try {
      await this.props.validateConfiguration();
      this.setState({
        showPriceOverview: !this.state.showPriceOverview,
        hasErrorReceiveOfferForm: false,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Error while validating configuration: ${e}`);
    }
  };

  handleClose = () => {
    this.setState({
      showPriceOverview: false,
      bulkNameErrors: [],
      approval: {
        value: false,
        error: null,
      },
      showErrors: false,
    });
    this.props.onClose();
  };

  onReceiveOfferFormPress = async () => {
    const formValidation = this.receiveOfferFormComponentRef.current.validate();
    if (!this.requestHasErrors() && formValidation) {
      const result = await this.props.sendReceiveOfferRequest(formValidation);
      if (!result) {
        this.setState({ hasErrorReceiveOfferForm: true });
        this.dialogContentRef.current.scrollTo(0, 0);
      }
      if (result) {
        this.setState({ hasErrorReceiveOfferForm: false });
      }
    } else {
      this.verifyRequestErrors(formValidation.hasError);
    }
  };

  setConfigurationCommentDebounced = debounce((value) => this.props.setConfigurationComment(value), 700);

  handleBasketNoticeChange = (event) => {
    this.setState({ notice: event.target.value });
    this.setConfigurationCommentDebounced(event.target.value);
  };

  onSelectableChanged = (priceData) => (key, newSelected) => {
    const nextData = priceData.map((currentElement) => {
      if (currentElement.key === key) {
        return {
          ...currentElement,
          selected: newSelected,
          price: newSelected ? '10,00 €' : '0,00 €',
        };
      }
      return currentElement;
    });
    this.setState({ priceData: nextData });
  };

  priceListClick = () => {
    this.priceListRef.current.scrollIntoView();
  };

  showPriceListButton = (inView) => {
    this.setState({ inView: inView });
  };

  saveAndReturn = () => {
    this.props.saveConfiguration();
  };

  renderPriceOverview() {
    const { notice, showPriceOverview, approval, inView, showErrors, bulkNameErrors, dataPrivacyAccepted } =
      this.state;
    const {
      amount,
      priceList,
      priceFormatted,
      onSelectableChanged,
      showProductVariants,
      ProductVariantComponent,
      error,
      width,
      hidePrices,
      title,
      classes,
      showReceiveOfferForm,
      vatNotice,
      clientTexts,
      displayAcceptDesign,
      notifications,
      showCallToAction,
      ReceiveOfferFormComponent,
      AcceptDesignComponent,
      PriceOverviewComponent,
      NotificationComponent,
      PriceListComponent,
      AddToCartComponent,
      AcceptPrivacyComponent,
    } = this.props;

    const { termsAndConditionsLink, dataPrivacyLink } = clientTexts;
    const mergedNotifications = [...notifications] || [];

    if (showErrors && bulkNameErrors && bulkNameErrors.length > 0) {
      bulkNameErrors.map((error) => {
        mergedNotifications.push({
          message: error,
          level: 'error',
          color: 'error',
        });
      });
    }

    return (
      <Dialog
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        classes={{
          paper: classes.dialogWrapper,
          container: width === 'md' || width === 'xs' ? classes.dialogPositioning : '',
          root: classes.dialogPositioning,
        }}
        BackdropProps={{ className: classes.dialogPositioning }}
        onClose={this.handleClose.bind(this)}
        open={showPriceOverview}
        maxWidth={'md'}
        fullWidth
        fullScreen={width === 'sm' || width === 'xs'}
        scroll="paper"
      >
        <LoadingOverlay show={this.props.addToCartPopUpLoader} />
        <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
          <Typography variant="body1">
            <b>{t(showReceiveOfferForm ? 'receiveOfferForm.sendButton' : 'amountPrice.intoCart')}</b>
          </Typography>
          <Icon className={classes.closeIcon} onClick={this.handleClose.bind(this)}>
            close
          </Icon>
        </DialogTitle>
        <RootRef rootRef={this.dialogContentRef}>
          <DialogContent classes={{ root: classes.contentPositioning }}>
            {this.state.hasErrorReceiveOfferForm && (
              <Typography color="error" variant="body2">
                {t('receiveOfferForm.checkInputsInForm')}
              </Typography>
            )}
            <PriceOverviewComponent
              title={t('amountPrice.productVariants')}
              contactData={t('amountPrice.contactData')}
              handleClose={this.handleClose}
              amount={amount}
              acceptDesignComponent={
                showCallToAction ? (
                  <div ref={this.acceptDesignRef}>
                    <AcceptDesignComponent
                      termsAndConditionsLink={termsAndConditionsLink}
                      displayAcceptDesign={displayAcceptDesign}
                      handleChangeCheckboxApproval={this.handleChange}
                      approval={approval}
                      closeProductOverviewDialog={this.handleClose}
                      notice={notice}
                      noteLabel={t('amountPrice.notice')}
                      handleChange={this.handleBasketNoticeChange}
                    />
                  </div>
                ) : null
              }
              acceptDataPrivacyComponent={
                showReceiveOfferForm && dataPrivacyLink ? (
                  <div ref={this.acceptDataPrivacyRef}>
                    <AcceptPrivacyComponent
                      dataPrivacyLink={dataPrivacyLink}
                      displayAcceptDesign={true}
                      handleChangeCheckboxApproval={this.handleChange}
                      approval={dataPrivacyAccepted}
                      closeProductOverviewDialog={this.handleClose}
                      displayNote={false}
                    />
                  </div>
                ) : null
              }
              notificationComponent={mergedNotifications.map(({ message, level, color }, key) => {
                return <NotificationComponent key={key} icon={level} color={color} notification={message} />;
              })}
              priceListComponent={
                <div ref={this.priceListRef}>
                  <InView as="div" onChange={(inView) => this.showPriceListButton(inView)}>
                    <PriceListComponent
                      amount={amount}
                      list={priceList}
                      totalPrice={priceFormatted}
                      taxNote={t('amountPrice.vatNotice')}
                      onSelectableChanged={onSelectableChanged}
                      hidePrices={hidePrices}
                      showReceiveOfferForm={showReceiveOfferForm}
                      productTitle={title}
                    />
                  </InView>
                </div>
              }
              productVariantComponent={showProductVariants && <ProductVariantComponent />}
              receiveOfferFormComponent={
                <ReceiveOfferFormComponent innerRef={this.receiveOfferFormComponentRef} />
              }
              showReceiveOfferForm={showReceiveOfferForm}
            />
          </DialogContent>
        </RootRef>
        <DialogActionsWrapper onTouchMove={this.handleTouchMove}>
          <AddToCartComponent
            buttonLabel={t(showReceiveOfferForm ? 'receiveOfferForm.sendButton' : 'amountPrice.intoCart')}
            showCallToAction={showCallToAction}
            onPress={showReceiveOfferForm ? this.onReceiveOfferFormPress : this.onAddToBasket}
            handleChange={this.handleBasketNoticeChange}
            disabled={!!error}
            totalPrice={priceFormatted}
            taxNote={vatNotice}
            showPriceList={!inView}
            priceListLabel={t('amountPrice.showPriceList')}
            priceListClick={this.priceListClick}
            hidePrices={hidePrices}
            amount={amount}
            showReceiveOfferForm={showReceiveOfferForm}
          />
        </DialogActionsWrapper>
      </Dialog>
    );
  }

  renderDefaultPriceBox = () => {
    const {
      classes,
      amount,
      boxed,
      priceFormatted,
      hidePrices,
      hidePieces,
      vatNotice,
      showReceiveOfferForm,
      showCallToAction,
      showConfigurationModeSwitch,
      ConfigurationModeSwitchComponent,
      bulkPrices,
      netTotalFormatted,
      orientation,
    } = this.props;

    const displayNetPrices = getConf('displayNetPrices', false);
    const boxStyle = boxed ? classes.cardBulk : classes.cardUnboxedBulk;

    let currentPrice = displayNetPrices ? netTotalFormatted : priceFormatted;

    for (let i = 0; i < bulkPrices.length; i++) {
      if (bulkPrices[i].amountFrom <= amount) {
        currentPrice = displayNetPrices ? bulkPrices[i].netTotalFormatted : bulkPrices[i].totalFormatted;
      }
    }

    return (
      <Card className={clsx(boxStyle, customClassName('cta-box'))}>
        {showCallToAction && orientation === 'portrait' && <div className={classes.topSpacer}></div>}
        <Grid item xs={12} sm={12} class={clsx(classes.bulkOuterGrid)}>
          <CardContent className={classes.cardContentWrapper} classes={{ root: classes.AmountPriceContent }}>
            {orientation === 'portrait' && (
              <ButtonGroup className={classes.mblButtonGroup}>
                {showCallToAction && orientation === 'portrait' && (
                  <Grid item xs={showConfigurationModeSwitch ? 6 : 12}>
                    <AnalyticsWrapper targetId="add_to_cart">
                      <MainButton
                        className={customClassName('cta-button')}
                        variant="contained"
                        fullWidth
                        size="medium"
                        onClick={this.openPriceOverview}
                        classes={{
                          root: classes.mainButton,
                          label: classes.mainButtonLabel,
                        }}
                      >
                        {t(showReceiveOfferForm ? 'receiveOfferForm.sendButton' : 'amountPrice.intoCart')}
                      </MainButton>
                    </AnalyticsWrapper>
                  </Grid>
                )}
                <Button
                  variant="text"
                  onClick={this.saveAndReturn}
                  classes={{
                    root: classes.backButton,
                  }}
                >
                  {t('amountPrice.back')}
                </Button>
              </ButtonGroup>
            )}

            {!hidePrices && (
              <>
                <Typography className={clsx(classes.bulkPriceYourCurrentPrice)}>
                  {t('bulkPrice.yourCurrentPrice')}
                </Typography>
                <Typography className={clsx(classes.bulkPriceCurrentPrice)}>{currentPrice}</Typography>
                <CardContent className={clsx(classes.bulkPriceContent)}>
                  {bulkPrices.map((o, i) => (
                    <div
                      key={`bulkPrice_${o.id}`}
                      className={clsx(
                        i === 0
                          ? classes.bulkPriceRowFirst
                          : i === bulkPrices.length - 1
                          ? classes.bulkPriceRowLast
                          : classes.bulkPriceRow
                      )}
                    >
                      <Typography className={clsx(classes.bulkPriceAmountFrom)}>
                        {t('bulkPrice.amountFrom', { amount: o.amountFrom })}
                      </Typography>
                      <Typography className={clsx(classes.bulkPriceTotal)}>
                        {displayNetPrices ? o.netTotalFormatted : o.totalFormatted}
                      </Typography>
                    </div>
                  ))}
                </CardContent>
                {!hidePieces && (
                  <>
                    <Grid container>
                      <Grid item xs={6} sm={6} md={5} lg={5}>
                        {showCallToAction && (
                          <Button
                            variant="text"
                            size="small"
                            onClick={this.openPriceOverview}
                            classes={{
                              root: classes.AmountPriceButton,
                              sizeSmall: classes.smallButtonSize,
                            }}
                          >
                            {amount} {t('amountPrice.pieces')}
                          </Button>
                        )}
                      </Grid>

                      <Grid item xs={6} sm={6} md={7} lg={7}>
                        <Typography className={clsx(classes.bulkPriceYourCurrentNetTotal)}>
                          {t('bulkPrice.netTotalPrice')}
                        </Typography>
                        <Typography className={clsx(classes.bulkPriceNetTotalFormatted)}>
                          {displayNetPrices ? netTotalFormatted : priceFormatted}
                        </Typography>
                      </Grid>
                    </Grid>
                  </>
                )}
              </>
            )}
            <Grid container>
              <Grid item xs={6} sm={6} md={5} lg={5} />
              <Grid item xs={6} sm={6} md={7} lg={7}>
                {priceFormatted && (
                  <Typography
                    className={classes.pos}
                    variant="caption"
                    classes={{
                      root: classes.AmountPriceTypographyVat,
                    }}
                  >
                    {vatNotice}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Grid>
        <Grid item xs={9} sm={12}>
          <CardActions className={classes.cardActions}>
            <Grid container spacing={1}>
              {showConfigurationModeSwitch && (
                <Grid item xs={showCallToAction ? 6 : 12}>
                  <ConfigurationModeSwitchComponent
                    className={customClassName('configuration-type-button')}
                    classes={{
                      root: classes.mainButton,
                      label: classes.mainButtonLabel,
                    }}
                  />
                </Grid>
              )}
              {showCallToAction && orientation === 'landscape' && (
                <Grid item xs={showConfigurationModeSwitch ? 6 : 12}>
                  <AnalyticsWrapper targetId="add_to_cart">
                    <MainButton
                      className={customClassName('cta-button')}
                      variant="contained"
                      fullWidth
                      size="medium"
                      onClick={this.openPriceOverview}
                      classes={{
                        root: classes.mainButton,
                        label: classes.mainButtonLabel,
                      }}
                    >
                      {t(showReceiveOfferForm ? 'receiveOfferForm.sendButton' : 'amountPrice.intoCart')}
                    </MainButton>
                  </AnalyticsWrapper>
                </Grid>
              )}
            </Grid>
          </CardActions>
        </Grid>
      </Card>
    );
  };

  render() {
    const { wrapperClass, renderPriceBox, classes, orientation, ...props } = this.props;

    const { showPriceOverview } = this.state;
    const { openPriceOverview, renderDefaultPriceBox } = this;

    return (
      <div className={clsx(wrapperClass, classes.wrapper)}>
        {renderPriceBox
          ? renderPriceBox({ ...props, openPriceOverview, renderDefaultPriceBox })
          : this.renderDefaultPriceBox()}
        {showPriceOverview && this.renderPriceOverview()}
        {orientation === 'landscape' && (
          <Button
            variant="text"
            onClick={this.saveAndReturn}
            classes={{
              root: classes.backButton,
            }}
          >
            {t('amountPrice.back')}
          </Button>
        )}
      </div>
    );
  }
}

export default withWidth()(withStyles(styles, { name: 'AmountPrice' })(AmountPrice));
